import { Component, Input, Output, EventEmitter } from "@angular/core";
import { StatusService } from "../../../services/status.service";
import { UsersService } from "../../../services/users.service";
import { BranchService } from "../../../services/branch.service";
import { PrioritiesService } from "../../../services/priorities.service";
import { DepartmentsService } from "../../../services/departaments.service";
import { FilterService } from "../../../services/filter.service";

type FilterCode =
    | "by.id"
    | "by.title"
    | "by.assigned"
    | "by.branch"
    | "by.department"
    | "by.registered"
    | "by.status"
    | "by.priority"
    | "newest"
    | "active"
    | "archived"
    | "not";

interface PillDef {
    parentFilter: any;
    valueIndex: number | null;
    displayValue: string;
    cssClass: string;
    /** The value's own colour where it has one, over the colour of its kind. */
    color: string | null;
}

@Component({
    selector: "selected-filters",
    templateUrl: "./selected-filters.component.html",
    styleUrls: ["./selected-filters.component.css"],
})
export class SelectedFiltersComponent {
    @Input() selectedFilters: any[] = [];
    @Output() removeFilter = new EventEmitter<{ filter: any; valueIndex: number | null }>();

    constructor(
        private priority: PrioritiesService,
        private department: DepartmentsService,
        private branchService: BranchService,
        private userService: UsersService,
        private statusService: StatusService,
        private filterService: FilterService
    ) { }

    onRemovePill(pill: PillDef): void {
        this.removeFilter.emit({ filter: pill.parentFilter, valueIndex: pill.valueIndex });
    }

    get flatPills(): PillDef[] {
        const pills: PillDef[] = [];
        for (const filter of this.visibleFilters) {
            if (filter.code === 'match.any' && filter.filters?.length) {
                for (let i = 0; i < filter.filters.length; i++) {
                    pills.push({
                        parentFilter: filter,
                        valueIndex: i,
                        displayValue: this.leafDisplayValue(filter.filters[i]),
                        cssClass: this.getFilterClass(filter),
                        color: this.valueColor(filter.filters[i]),
                    });
                }
            } else if (filter.code === 'not' && filter.filters?.[0]?.code === 'match.any' && filter.filters[0].filters?.length) {
                const inner = filter.filters[0];
                for (let i = 0; i < inner.filters.length; i++) {
                    pills.push({
                        parentFilter: filter,
                        valueIndex: i,
                        displayValue: `Niet ${this.leafDisplayValue(inner.filters[i])}`,
                        cssClass: this.getFilterClass(filter),
                        color: this.valueColor(inner.filters[i]),
                    });
                }
            } else {
                pills.push({
                    parentFilter: filter,
                    valueIndex: null,
                    displayValue: this.getFilterDisplayValue(filter),
                    cssClass: this.getFilterClass(filter),
                    color: this.valueColor(filter.code === 'not' ? filter.filters?.[0] : filter),
                });
            }
        }
        return pills;
    }

    /**
     * A status or a priority has a colour of its own, which the ticket list
     * shows it in, so its chip takes that colour rather than the colour of its
     * kind: "Klaar" is the same green in the chip as in the rows it selects.
     * The rest - a title, a number, a person - has no colour of its own and
     * keeps the colour of its kind. A department does the same through its
     * class, and a status or priority the service does not know falls back.
     */
    private valueColor(leaf: any): string | null {
        let color = "";
        if (leaf?.code === 'by.status') {
            color = this.statusService.colorForId(leaf.id);
        } else if (leaf?.code === 'by.priority') {
            color = this.priority.colorForId(leaf.id);
        }
        return color && color.toLowerCase() !== "#ffffff" ? color : null;
    }

    private leafDisplayValue(leaf: any): string {
        switch (leaf.code) {
            case 'by.registered':
            case 'by.assigned':
                return this.userService.labelForId(leaf.id);
            case 'by.branch':
                return this.branchService.labelForId(leaf.id);
            case 'by.department':
                return this.department.labelForId(leaf.id);
            case 'by.status':
                return this.statusService.labelForId(leaf.id);
            case 'by.priority':
                return this.priority.labelForId(leaf.id);
            case 'by.title':
                return leaf.searchphrase;
            case 'by.id':
                return String(leaf.id);
            default:
                return leaf.name || leaf.code;
        }
    }
    getFilterClass(filter: any): string {
        // the colours live in styles.css, shared with the palette
        const kindClassMap: { [key in FilterCode]: string } = {
            "by.id": "filter-kind-id",
            "by.title": "filter-kind-title",
            "by.assigned": "filter-kind-assigned",
            "by.branch": "filter-kind-branch",
            "by.department": "filter-kind-department",
            "by.registered": "filter-kind-registered",
            "by.status": "filter-kind-status",
            "by.priority": "filter-kind-priority",
            newest: "filter-kind-newest",
            active: "filter-kind-active",
            archived: "filter-kind-archived",
            not: "filter-kind-not",
        };

        if (filter.code === "not" && filter.filters?.length) {
            const inner = filter.filters[0];
            const innerClass = inner.code === "match.any"
                ? this.getFilterClass(inner)
                : (kindClassMap[inner.code as FilterCode] || "");
            return kindClassMap["not"] + " " + innerClass;
        }

        if (filter.code === "match.any" && filter.filters?.length) {
            const type = filter.filters[0].code;
            const sameType = filter.filters.every((f: any) => f.code === type);
            if (sameType && kindClassMap[type as FilterCode]) {
                return kindClassMap[type as FilterCode];
            }
        }

        return kindClassMap[filter.code as FilterCode] || "";
    }

    getFilterDisplayValue(filter: any): string {
        if (filter.code === "not" && filter.filters?.length) {
            const innerFilter = filter.filters[0];
            const innerLabel = this.getFilterDisplayValue(innerFilter);
            return `Niet ${innerLabel}`;
        }

        if (filter.code === "match.any" && filter.filters?.length) {
            const type = filter.filters[0]?.code;

            switch (type) {
                case "by.registered":
                    return filter.filters
                        .map((f: any) => this.userService.labelForId(f.id))
                        .join(", ");
                case "by.assigned":
                    return filter.filters
                        .map((f: any) => this.userService.labelForId(f.id))
                        .join(", ");
                case "by.branch":
                    return filter.filters
                        .map((f: any) => this.branchService.labelForId(f.id))
                        .join(", ");
                case "by.department":
                    return filter.filters
                        .map((f: any) => this.department.labelForId(f.id))
                        .join(", ");
                case "by.status":
                    return filter.filters
                        .map((f: any) => this.statusService.labelForId(f.id))
                        .join(", ");
                case "by.priority":
                    return filter.filters
                        .map((f: any) => this.priority.labelForId(f.id))
                        .join(", ");
                case "by.id":
                    return filter.filters.map((f: any) => f.id).join(", ");
                case "by.title":
                    return filter.filters.map((f: any) => f.searchphrase).join(", ");
            }
        }

        switch (filter.code) {
            case "by.id":
                return filter.id;
            case "by.title":
                return filter.searchphrase;
            case "by.assigned":
                return this.userService.labelForId(filter.id);
            case "by.branch":
                return this.branchService.labelForId(filter.id);
            case "by.department":
                return this.department.labelForId(filter.id);
            case "by.registered":
                return this.userService.labelForId(filter.id);
            case "by.status":
                return this.statusService.labelForId(filter.id);
            case "by.priority":
                return this.priority.labelForId(filter.id);
            case "newest":
                return filter.top;
            case "active":
                return filter.active;
            case "archived":
                return filter.archived;
            default:
                return filter.name || filter.code;
        }
    }
    get visibleFilters() {
        // the standard filter's own two parts are not shown: the user did not
        // put them there. The rule lives in the filter service, which also has
        // to know which filters are the standard one in order to drop them.
        return this.selectedFilters?.filter(
            (f) => !this.filterService.isStandardFilterPart(f)
        ) || [];
    }
}
